import cn from "classnames";
import Image from "next/image";

import { ImgBgResponsive } from "@/src/component/base/ImgBgResponsive";
import { ProviderHooks } from "@/src/hook";
import { StrapiComponentType, ComponentType } from "@/src/type";

import { GapBreakpointModule, ImageSizedModule, SectionModule, TextBreakpointModule } from "../component";
import FooterSimpleColumn from "./FooterSimpleColumn";

import styles from "./FooterSimple.module.scss";
import { LoadFromCDN } from "@/src/util";

interface FooterSimpleProps extends ComponentType.DynamicPageSectionComponentProps<StrapiComponentType.Section.FooterSimple> {}

function FooterSimple({ section, slug, snapToGrid, classes, searchParams, quality, layout, lazy }: FooterSimpleProps) {
  const cms = ProviderHooks.useCMS();
  const footer: StrapiComponentType.Section.FooterSimple = section.type?.[0];
  const topOn = footer.topLogoOn || footer.titleTextOn || (footer.columns ?? []).length > 0;
  const bottomOnly = !topOn && !footer.titleTextOn;
  const defaultCopyrightText = `Copyright ${new Date().getFullYear()} FOL Management LLC. All rights reserved.`;
  const copyrightText = (footer.copyrightText ?? defaultCopyrightText).replace("${year}", `${new Date().getFullYear()}`);

  function numberOfColumns() {
    const columns = (footer.columns ?? []).length;
    switch (columns) {
      case 3:
        return styles.three;
      case 5:
        return styles.five;
      default:
        return undefined;
    }
  }

  function navLink(isExternal?: boolean, href?: string) {
    return isExternal ? href : cms.constructLink(href ?? "");
  }

  return (
    <SectionModule
      section={section}
      slug={slug}
      classes={{
        section: classes?.section,
        styledContainer: cn(styles.footer, styles.snapToGrid, bottomOnly ? styles.bottomOnly : undefined, !snapToGrid ? styles.fullPage : undefined),
      }}
      stylePrefix="FooterSimple"
      styleContainerId={footer.id}
      snapToGrid={snapToGrid}
      tag="footer"
      searchParams={searchParams}
      quality={quality}
      layout={layout}
      lazy={lazy}
    >
      <GapBreakpointModule
        id={footer.id}
        stylePrefix="FooterSimple"
        className={styles.content}
        gapBreakpoints={footer.gapBreakpoints}
      >
        {topOn && (
          <div className={cn(styles.top, !snapToGrid ? styles.fullPage : undefined)}>
            {footer.topLogoOn && (
              <Image
                className={styles.footerLogo}
                src={footer.topLogo?.data?.attributes?.url ?? "/image/icon_wave_large.svg"}
                loader={footer.topLogo?.data?.attributes?.url ? LoadFromCDN.loaderForNextJS : undefined}
                width={footer.topLogo?.data?.attributes?.width ?? 150}
                height={footer.topLogo?.data?.attributes?.height ?? 80}
                alt={footer.topLogo?.data?.attributes?.alternativeText ?? "Flow Footer Logo"}
              />
            )}
            {footer.otherImage && (
              <ImageSizedModule
                id={`footer_top_logo_${footer.id}`}
                image={footer.otherImage}
                quality={quality}
              />
            )}
            {footer.titleTextOn && (
              <>
                <TextBreakpointModule
                  id={`footer_title_${footer.id}`}
                  text={section.title}
                  className={styles.title}
                />
                <TextBreakpointModule
                  id={`footer_under_title_${footer.id}`}
                  text={footer.underTitle}
                  className={cn(styles.title, styles.under)}
                />
              </>
            )}
            {(footer.columns ?? []).length > 0 && (
              <div className={cn(styles.columns, numberOfColumns())}>
                {(footer.columns ?? []).map((column, i) => (
                  <FooterSimpleColumn
                    key={i}
                    column={column}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        {footer.legalDisclaimersOn && (
          <div className={cn(styles.bottom, !snapToGrid ? styles.pagePadding : undefined)}>
            {footer.bottomLogoOn && (
              <ImgBgResponsive
                imageUrl={"/image/logo_flow_color_wide.svg"}
                className={styles.logo}
                lazy="lazy"
                prefetch={false}
              />
            )}
            {footer.legalDisclaimersOn && (
              <div className={cn(styles.rights, !footer.bottomLogoOn ? styles.logoOff : undefined)}>
                <div className={styles.copyright}>{copyrightText}</div>
                <div className={cn(styles.divider, styles.dividerCopyright)}>|</div>
                <div className={styles.legal}>
                  <a
                    href={navLink(footer?.terms?.isExternal, footer?.terms?.href)}
                    target={footer?.terms?.target ?? "_self"}
                  >
                    {footer?.terms?.label ?? "Terms & Conditions"}
                  </a>
                  <div className={styles.divider}>|</div>
                  <a
                    href={navLink(footer?.privacyPolicy?.isExternal, footer?.privacyPolicy?.href)}
                    target={footer?.privacyPolicy?.target ?? "_self"}
                  >
                    {footer?.privacyPolicy?.label ?? "Privacy Policy"}
                  </a>
                  {footer?.otherLegal && (
                    <>
                      <div className={styles.divider}>|</div>
                      <a
                        href={navLink(footer?.otherLegal?.isExternal, footer?.otherLegal?.href)}
                        target={footer?.otherLegal?.target ?? "_self"}
                      >
                        {footer?.otherLegal?.label ?? "Legal Disclaimers"}
                      </a>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </GapBreakpointModule>
    </SectionModule>
  );
}

export default FooterSimple;
